import { PlayIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';
import { isVideoUrl } from '@/utils/mediaType';

export { isVideoUrl };

/** First animated clip in a style's media list, if it has one. */
export const findStyleVideoUrl = (
  images?: { url?: string }[],
): string | undefined => images?.find((image) => isVideoUrl(image.url))?.url;

interface StylePreviewMediaProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  loading?: 'lazy' | 'eager';
}

export const StylePreviewMedia = ({
  src,
  alt,
  width,
  height,
  className,
  loading,
}: StylePreviewMediaProps) => {
  if (isVideoUrl(src)) {
    return (
      <video
        src={src}
        width={width}
        height={height}
        className={className}
        autoPlay
        loop
        muted
        playsInline
        preload='metadata'
        aria-label={alt}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
    />
  );
};

/**
 * A preview tile: the media, the inset ring, and — when the source is a clip
 * rather than a still — a "Video" chip so the motion reads as deliberate
 * instead of looking like a page glitch. The chip sits outside the media
 * element so the media's hover zoom doesn't drag it around.
 */
const StylePreviewTile = ({
  src,
  alt,
  /** Height cap for clips; the default suits the hero column. */
  videoMaxHeightClassName = 'max-h-[24rem] sm:max-h-[30rem]',
}: {
  src: string;
  alt: string;
  videoMaxHeightClassName?: string;
}) => {
  const { t } = useLocale('ai-image-generator');
  const isVideo = isVideoUrl(src);

  return (
    // Video tiles shrink-wrap so the ring and the chip track the clip's real
    // box — a portrait clip is narrower than the column it sits in.
    <div className={clsxm('relative', isVideo && 'mx-auto w-fit')}>
      <StylePreviewMedia
        width={400}
        height={400}
        src={src}
        alt={alt}
        className={clsxm(
          'rounded-3xl bg-gray-900/5 shadow-xl transition-transform duration-500 hover:scale-105',
          // Clips keep their own aspect ratio — cropping a 16:9 or portrait
          // clip to the square tile threw away the half of the shot that
          // mattered. Sizing both axes `auto` lets the width cap drive
          // landscape clips (which would otherwise be a thin strip in a
          // column sized for a square) and the height cap drive portrait
          // ones (which would otherwise run past the fold), with the aspect
          // ratio preserved either way. Stills keep object-cover.
          isVideo
            ? clsxm('h-auto w-auto max-w-full', videoMaxHeightClassName)
            : 'w-full object-cover',
        )}
      />
      <div className='pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-gray-900/10' />
      {isVideo && (
        <span className='pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gray-900/70 px-2 py-1 text-[11px] font-medium leading-none text-white shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-sm'>
          <PlayIcon className='h-3 w-3' aria-hidden='true' />
          {t('Video')}
        </span>
      )}
    </div>
  );
};

export default StylePreviewTile;
